import jspdf, { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import {} from './fonts/Montserrat-SemiBold-normal';
import {} from './fonts/Montserrat-Light-normal';
import {} from './fonts/Montserrat-Medium-normal';
import { KPI } from '@esg/esg-global-types';
import { ResponseExtended } from '../readiness_gauge/readiness_gauge_instance';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { getKPIs, KPIExtended } from '../readiness_gauge/kpi';
import {
  convert_html_sizing_to_points,
  generateHeadingUnderline,
  generatePageBackground
} from './util';
import { GaugeScores, generateSyntiroGaugeFrontPage } from './utils/readiness_pdf_conference';

interface GroupedResponses {
  [key: string]: Array<ResponseExtended>;
}

// Global location and line weight data
const A4_WIDTH = 595;
const A4_HEIGHT = 842;
const MARGIN = 10;
const RESPONSE_ICON_LINE_THICKNESS = 1.5;
const DEFAULT_LINE_THICKNESS = 0.2;
const MAIN_HEADING_Y_LOCATION = 50;

// Colors used in report
const PRIMARY_COLOR = '#0E3A5A';
const POSITIVE_GREEN = '#3F7E44';
const NEGATIVE_RED = '#FF3A21';
const BACKGROUND_COLOR = '#F9FCFF';

/**
 * Generates the Check or Cross icon for a question response.
 * @param {jsPDF} pdf_document Current PDF document object being created.
 * @param {number} response_status 1 or 0 value representing Yes or No response.
 * @param {string} y_location Y Coordinate for start the response icon.
 * @returns {void}
 */
const generateResponseIcon = (
  pdf_document: jsPDF,
  response_status: number,
  y_location: number
): void => {
  try {
    const response_initial_x_offset: number = (A4_WIDTH / 8) * 6;
    const circle_x_location: number = response_initial_x_offset + 40 + 30;
    const response_line_initial_offset: number = response_initial_x_offset + 35;
    if (response_status) {
      pdf_document.setLineWidth(RESPONSE_ICON_LINE_THICKNESS);
      pdf_document.setDrawColor(POSITIVE_GREEN);
      pdf_document.circle(circle_x_location, y_location, 10);
      pdf_document.line(
        response_line_initial_offset + 27,
        y_location,
        response_line_initial_offset + 32.5,
        y_location + 5
      );
      pdf_document.line(
        response_line_initial_offset + 32.5,
        y_location + 5,
        response_initial_x_offset + 45 + 30,
        y_location - 5
      );
      pdf_document.setLineWidth(DEFAULT_LINE_THICKNESS);
    } else {
      pdf_document.setLineWidth(RESPONSE_ICON_LINE_THICKNESS);
      pdf_document.setDrawColor(NEGATIVE_RED);
      pdf_document.circle(circle_x_location, y_location, 10);
      pdf_document.line(
        response_line_initial_offset + 30,
        y_location - 5,
        response_initial_x_offset + 45 + 30,
        y_location + 5
      );
      pdf_document.line(
        response_initial_x_offset + 45 + 30,
        y_location - 5,
        response_line_initial_offset + 30,
        y_location + 5
      );
      pdf_document.setLineWidth(DEFAULT_LINE_THICKNESS);
    }
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: generateReponseIcon failed on an unknown error.',
      {
        pdf_document: pdf_document,
        response_status: response_status,
        y_location: y_location
      },
      tracking_id
    );
  }
};

/**
 * Generates all pages containing results to KPI questions.
 * @param {jsPDF} pdf_document Current PDF document object being created.
 * @param {GroupedResponses} question_result_list List of question results to generate.
 * @param {string} group_name Name of current group kpis are being generated for.
 * @param {Array<KPIExtended>} kpis List of available KPIS from master list.
 * @returns {void}
 */
const generateResultsPages = (
  pdf_document: jsPDF,
  question_result_list: GroupedResponses,
  group_name: string,
  kpis: Array<KPIExtended>
) => {
  try {
    const calculated_result_x_location = (A4_WIDTH / 8) * 6 + 40;
    let group_name_printed = false;
    let start_of_question_text = MAIN_HEADING_Y_LOCATION + 30;
    const question_text_x_location = 30;
    const FINAL_HEIGHT_LIMIT = A4_HEIGHT - 65;
    pdf_document.addPage();
    for (const kpi of kpis.filter(
      (kpi) => Object.keys(question_result_list).indexOf(kpi.name) !== -1
    )) {
      const response_list = question_result_list[kpi.name];
      if (start_of_question_text > FINAL_HEIGHT_LIMIT - 45) {
        pdf_document.addPage();
        start_of_question_text = MAIN_HEADING_Y_LOCATION;
      }
      if (!group_name_printed) {
        pdf_document.setFontSize(20);
        pdf_document.setFont('Montserrat-Medium', 'normal');
        pdf_document.text(group_name, A4_WIDTH / 2, MAIN_HEADING_Y_LOCATION, { align: 'center' });
        generateHeadingUnderline(
          pdf_document,
          PRIMARY_COLOR,
          MAIN_HEADING_Y_LOCATION,
          A4_WIDTH,
          MARGIN
        );
        group_name_printed = true;
      }
      pdf_document.setFont('Montserrat-Light', 'normal');
      pdf_document.setFontSize(15);
      pdf_document.text(kpi.name, question_text_x_location, start_of_question_text);
      pdf_document.setDrawColor(PRIMARY_COLOR);
      pdf_document.line(
        (A4_WIDTH / 8) * 6,
        start_of_question_text - 15,
        (A4_WIDTH / 8) * 6,
        start_of_question_text + 5
      );
      const completed_percentage = response_list.reduce(
        (totalYes, currentResponse) => totalYes + currentResponse.response,
        0
      );
      pdf_document.text(
        `${((completed_percentage / response_list.length) * 100).toFixed(0)}% (${completed_percentage}/${response_list.length})`,
        calculated_result_x_location,
        start_of_question_text
      );
      start_of_question_text = start_of_question_text + 45;
      response_list.forEach((response, index) => {
        if (start_of_question_text > FINAL_HEIGHT_LIMIT) {
          pdf_document.addPage();
          start_of_question_text = MAIN_HEADING_Y_LOCATION;
        }
        pdf_document.setFontSize(10);
        const split_text: string = pdf_document.splitTextToSize(response.question.name, 400);
        pdf_document.text(split_text, question_text_x_location, start_of_question_text);
        index === response_list.length - 1
          ? pdf_document.setDrawColor(PRIMARY_COLOR)
          : pdf_document.setDrawColor(0, 0, 0, 0.1);
        pdf_document.line(
          question_text_x_location,
          start_of_question_text + 25,
          A4_WIDTH - question_text_x_location,
          start_of_question_text + 25
        );
        generateResponseIcon(pdf_document, response.response, start_of_question_text);

        start_of_question_text = start_of_question_text + 45;
      });
    }
    return;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: generateResultsPage failed on an unknown error.',
      {
        pdf_document: pdf_document,
        question_result_list: question_result_list,
        group_name: group_name
      },
      tracking_id
    );
  }
};

/**
 * Generates front page of report containing overall results and gauges.
 * @param {jsPDF} pdf_document Current PDF document object being created.
 * @param {string} report_name Name of report that pdf is being generated for.
 * @returns {void}
 */
const generateFrontPage = async (pdf_document: jsPDF, report_name?: string) => {
  try {
    const total_readiness_chart: HTMLElement | null = document.getElementById(
      'total-readiness-gauge-chart'
    );
    const total_readiness_percentage: HTMLElement | null = document.getElementById(
      'total-readiness-gauge-percentage'
    );
    const environmental_readiness_gauge: HTMLElement | null = document.getElementById(
      'environmental-readiness-gauge'
    );
    const social_readiness_gauge: HTMLElement | null =
      document.getElementById('social-readiness-gauge');
    const governance_readiness_gauge: HTMLElement | null = document.getElementById(
      'governance-readiness-gauge'
    );
    const total_readiness_chart_canvas: HTMLCanvasElement = await html2canvas(
      total_readiness_chart as HTMLElement,
      { backgroundColor: BACKGROUND_COLOR, logging: false }
    );
    const total_readiness_percentage_canvas: HTMLCanvasElement = await html2canvas(
      total_readiness_percentage as HTMLElement,
      { backgroundColor: BACKGROUND_COLOR, logging: false }
    );
    const environmental_readiness_gauge_canvas: HTMLCanvasElement = await html2canvas(
      environmental_readiness_gauge as HTMLElement,
      { backgroundColor: BACKGROUND_COLOR, logging: false }
    );
    const social_readiness_gauge_canvas: HTMLCanvasElement = await html2canvas(
      social_readiness_gauge as HTMLElement,
      { backgroundColor: BACKGROUND_COLOR, logging: false }
    );
    const governance_gauge_canvas: HTMLCanvasElement = await html2canvas(
      governance_readiness_gauge as HTMLElement,
      { backgroundColor: BACKGROUND_COLOR, logging: false }
    );

    generatePageBackground(pdf_document, '#F9FCFF', A4_WIDTH, A4_HEIGHT, 20);

    const environmental_gauge_image: string = environmental_readiness_gauge_canvas.toDataURL(
      'image/png',
      1.0
    );
    const social_gauge_image: string = social_readiness_gauge_canvas.toDataURL('image/png', 1.0);
    const governance_gauge_image: string = governance_gauge_canvas.toDataURL('image/png', 1.0);

    const GAUGE_STARTNG_POINT = 100;
    const MAIN_GAUGE_HEIGHT = 250;
    const MAIN_GAUGE_WIDTH = 350;
    const TOTAL_PERCENTAGE_HEIGHT = 48;
    const TOTAL_PERCENTAGE_WIDTH = 131;
    const MINI_GAUGE_WIDTH = 150;
    const MINI_GAUGE_HEIGHT = 186;
    const MINI_GAUGE_OFFSETS = 175;

    pdf_document.setFont('Montserrat-SemiBold', 'normal');
    pdf_document.setFontSize(25);
    pdf_document.setTextColor(PRIMARY_COLOR);
    const split_text: string = pdf_document.splitTextToSize(
      report_name ? `${report_name} Report` : 'Readiness Gauge Report',
      595
    );
    pdf_document.text(split_text, A4_WIDTH / 2, MAIN_HEADING_Y_LOCATION, { align: 'center' });

    pdf_document.addImage(
      total_readiness_chart_canvas,
      A4_WIDTH / 2 - convert_html_sizing_to_points(MAIN_GAUGE_WIDTH) / 2,
      GAUGE_STARTNG_POINT,
      convert_html_sizing_to_points(MAIN_GAUGE_WIDTH),
      convert_html_sizing_to_points(MAIN_GAUGE_HEIGHT)
    );

    pdf_document.setFont('Montserrat-Medium', 'normal');
    pdf_document.setFontSize(20);
    pdf_document.text(
      'Your ESG Readiness Score',
      A4_WIDTH / 2,
      GAUGE_STARTNG_POINT + convert_html_sizing_to_points(MAIN_GAUGE_HEIGHT) + 15,
      { align: 'center' }
    );
    pdf_document.addImage(
      total_readiness_percentage_canvas,
      A4_WIDTH / 2 - convert_html_sizing_to_points(TOTAL_PERCENTAGE_WIDTH) / 2,
      GAUGE_STARTNG_POINT +
        convert_html_sizing_to_points(MAIN_GAUGE_HEIGHT) +
        convert_html_sizing_to_points(40.8),
      convert_html_sizing_to_points(TOTAL_PERCENTAGE_WIDTH),
      convert_html_sizing_to_points(TOTAL_PERCENTAGE_HEIGHT)
    );

    const mini_guage_y_location = GAUGE_STARTNG_POINT + convert_html_sizing_to_points(342.81) + 50;
    const mini_gauge_initial_x = A4_WIDTH / 2 - convert_html_sizing_to_points(MINI_GAUGE_WIDTH) / 2;

    pdf_document.addImage(
      environmental_gauge_image,
      mini_gauge_initial_x - MINI_GAUGE_OFFSETS,
      mini_guage_y_location,
      convert_html_sizing_to_points(MINI_GAUGE_WIDTH),
      convert_html_sizing_to_points(MINI_GAUGE_HEIGHT)
    );
    pdf_document.addImage(
      social_gauge_image,
      mini_gauge_initial_x,
      mini_guage_y_location,
      convert_html_sizing_to_points(MINI_GAUGE_WIDTH),
      convert_html_sizing_to_points(MINI_GAUGE_HEIGHT)
    );
    pdf_document.addImage(
      governance_gauge_image,
      mini_gauge_initial_x + MINI_GAUGE_OFFSETS,
      mini_guage_y_location,
      convert_html_sizing_to_points(MINI_GAUGE_WIDTH),
      convert_html_sizing_to_points(MINI_GAUGE_HEIGHT)
    );

    return;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: generateFrontPage failed on an unknown error.',
      {
        pdf_document: pdf_document,
        report_name: report_name
      },
      tracking_id
    );
  }
};

/**
 * Function to group responses into their respective KPIS as Key Value pair.
 * @param {Array<ResponseExtended>} responses Raw Reponses array.
 * @returns {GroupedResponses}
 */
const groupResponsesToKpi = (responses: Array<ResponseExtended>): GroupedResponses => {
  try {
    const grouped_responses: GroupedResponses = {};
    responses.forEach((response: ResponseExtended) => {
      if (grouped_responses[(response.question.kpi as KPI).name]) {
        grouped_responses[(response.question.kpi as KPI).name] = [
          ...grouped_responses[(response.question.kpi as KPI).name],
          response
        ];
      } else {
        grouped_responses[(response.question.kpi as KPI).name] = [response];
      }
    });

    return grouped_responses;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: groupResponsesToKpi failed on an unknown error.',
      {
        responses: responses
      },
      tracking_id
    );
  }
};

/**
 * Generates PDF based on results of readiness gauge.
 * @param {string} parent_company_id Current app company generating PDF download, to be used temporarily for conditionally rendering the front page.
 * @param {string | undefined} report_name Name of readiness gauge report.
 * @param {Array<ResponseExtended> | undefined} readiness_gauge_responses List of responses to questionnaire.
 * @param {GaugeScores} scores Aggregated category (E,S,G) specific scores to show on the front page of the PDF.
 * @returns {Promise<void>}
 */
export const generatePdf = async (
  parent_company_id: string,
  report_name: string | undefined,
  readiness_gauge_responses: Array<ResponseExtended>,
  scores: GaugeScores
): Promise<void> => {
  try {
    if (!readiness_gauge_responses) {
      return;
    }

    const pdf_document: jsPDF = new jspdf({
      unit: 'pt',
      compress: true,
      format: 'a4'
    });

    if (parent_company_id === 'demo_gbcsa') {
      // This is explicitly only to be shown for the dedicated conference company. Any other company should get the old report front page.
      // Relevant KPIs to check.
      type KpiName =
        | 'Water'
        | 'Energy'
        | 'Environmental Policy'
        | 'Health and Safety'
        | 'Stakeholder Engagement'
        | 'Governance Policy';

      const utility_kpi_names: Array<KpiName> = ['Water', 'Energy'];
      const policy_kpi_names: Array<KpiName> = [
        'Environmental Policy',
        'Health and Safety',
        'Stakeholder Engagement',
        'Governance Policy'
      ];

      // Generic filtering function to reduce duplication.
      const getResponsesByKpiNames = (kpiNames: Array<KpiName>): Array<ResponseExtended> => {
        return readiness_gauge_responses.filter((response: ResponseExtended) =>
          kpiNames.includes((response.question.kpi as KPI).name as KpiName)
        );
      };

      // Specific response getters.
      const getUtilityResponses = (): Array<ResponseExtended> =>
        getResponsesByKpiNames(utility_kpi_names);

      const getPolicyResponses = (): Array<ResponseExtended> =>
        getResponsesByKpiNames(policy_kpi_names);

      // Generic compliance checker.
      const checkCompliance = (responses: Array<ResponseExtended>): boolean =>
        responses.every((response: ResponseExtended) => response.response === 1);

      // Specific compliance getters.
      const getUtilityManagementCompliance = (): boolean => {
        const utility_responses: Array<ResponseExtended> = getUtilityResponses();
        return checkCompliance(utility_responses);
      };

      const getPolicyCompliance = (): boolean => {
        const policy_responses: Array<ResponseExtended> = getPolicyResponses();
        return checkCompliance(policy_responses);
      };

      const has_policy: boolean = getPolicyCompliance();
      const has_environmental_initiative: boolean = getUtilityManagementCompliance();

      await generateSyntiroGaugeFrontPage(
        pdf_document,
        report_name,
        scores,
        has_policy,
        has_environmental_initiative
      );
    } else {
      await generateFrontPage(pdf_document, report_name);
    }

    const environmental_responses: GroupedResponses = await groupResponsesToKpi(
      readiness_gauge_responses.filter((question: ResponseExtended) =>
        question.question.categories.includes('Environmental')
      )
    );

    const social_responses: GroupedResponses = await groupResponsesToKpi(
      readiness_gauge_responses.filter((question: ResponseExtended) =>
        question.question.categories.includes('Social')
      )
    );
    const governance_responses: GroupedResponses = await groupResponsesToKpi(
      readiness_gauge_responses.filter((question: ResponseExtended) =>
        question.question.categories.includes('Governance')
      )
    );

    const kpis: Array<KPIExtended> = await getKPIs('readiness_gauge');
    kpis.sort((kpi_a: KPIExtended, kpi_b: KPIExtended) => kpi_a.order - kpi_b.order);
    generateResultsPages(pdf_document, environmental_responses, 'Environmental', kpis);
    generateResultsPages(pdf_document, social_responses, 'Social', kpis);
    generateResultsPages(pdf_document, governance_responses, 'Governance', kpis);

    pdf_document.save(`${report_name}.pdf`);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: generatePdf failed on an unknown error.',
      {
        report_name: report_name,
        readiness_gauge_responses: readiness_gauge_responses
      },
      tracking_id
    );
  }
};
